<template>
  <div class="min-h-screen bg-gray-50 font-body">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between flex-col md:flex-row gap-3">
          <div class="flex items-center space-x-4 space-x-reverse">
            <div class="relative">
              <img 
                src="@/assets/Logo.png" 
                alt="کبابی مارو" 
                class="navbar-logo w-12 h-12"
              />
            </div>
            <div>
              <h1 class="text-xl font-semibold text-gray-800">تنظیمات</h1>
              <p class="text-sm text-gray-500">کبابی مارو</p>
            </div>
          </div>
          
          <div class="flex items-center space-x-3 space-x-reverse flex-wrap justify-center md:justify-end">
            <button 
              @click="goToAdminPanel"
              class="bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors"
            >
              بازگشت به پنل
            </button>
            <button 
              @click="goToMenu"
              class="bg-brown-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brown-700 transition-colors"
            >
              مشاهده منو
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
      <div class="container mx-auto px-4 py-8">
      <!-- Loading State -->
      <div v-if="loading" class="flex justify-center items-center py-20">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-brown-600"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-20">
        <div class="text-red-600 text-lg mb-4">{{ error }}</div>
        <button 
          @click="loadData"
          class="bg-brown-600 text-white px-6 py-2 rounded-lg hover:bg-brown-700 transition-colors"
        >
          تلاش مجدد
        </button>
      </div>

      <!-- Content -->
      <div v-else class="space-y-8">
        <!-- Sections Management -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-2xl font-semibold text-gray-800">مدیریت بخش‌ها</h2>
            <button 
              @click="showSectionForm = true"
              class="bg-brown-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-brown-700 transition-colors"
            >
              افزودن بخش جدید
            </button>
          </div>

          <!-- Sections List -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div 
              v-for="section in sections" 
              :key="section.id"
              class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div class="flex items-center justify-between mb-3">
                <h3 class="font-semibold text-gray-800">{{ section.name }}</h3>
                <div class="flex space-x-2 space-x-reverse">
                  <button 
                    @click="editSection(section)"
                    class="text-blue-600 hover:text-blue-800 p-1"
                  >
                    ✏️
                  </button>
                  <button 
                    @click="deleteSection(section.id)"
                    class="text-red-600 hover:text-red-800 p-1"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <p class="text-gray-600 text-sm mb-2">{{ section.description }}</p>
              <div class="flex items-center justify-between text-xs text-gray-500">
                <span>{{ section.tables?.length || 0 }} میز</span>
                <span v-if="section.isActive" class="text-green-600">فعال</span>
                <span v-else class="text-red-600">غیرفعال</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Tables Management -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-2xl font-semibold text-gray-800">مدیریت میزها</h2>
            <button 
              @click="showTableForm = true"
              class="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              افزودن میز جدید
            </button>
          </div>

          <!-- Tables List -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div 
              v-for="table in tables" 
              :key="table.id"
              class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div class="flex items-center justify-between mb-3">
                <h3 class="font-semibold text-gray-800">{{ table.name }}</h3>
                <div class="flex space-x-2 space-x-reverse">
                  <button 
                    @click="editTable(table)"
                    class="text-blue-600 hover:text-blue-800 p-1"
                  >
                    ✏️
                  </button>
                  <button 
                    @click="deleteTable(table.id)"
                    class="text-red-600 hover:text-red-800 p-1"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <p class="text-gray-600 text-sm mb-2">{{ table.description }}</p>
              <div class="flex items-center justify-between text-xs text-gray-500">
                <span>ظرفیت: {{ table.capacity }} نفر</span>
                <span v-if="table.isActive" class="text-green-600">فعال</span>
                <span v-else class="text-red-600">غیرفعال</span>
              </div>
              <div class="text-xs text-gray-500 mt-1">
                بخش: {{ table.sectionName }}
              </div>
            </div>
          </div>
        </div>

        <!-- Employees Management -->
        <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-2xl font-semibold text-gray-800">مدیریت کارمندان</h2>
            <button 
              @click="showEmployeeForm = true"
              class="bg-purple-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              افزودن کارمند جدید
            </button>
          </div>

          <!-- Employees List -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div 
              v-for="employee in employees" 
              :key="employee.id"
              class="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div class="flex items-center justify-between mb-3">
                <h3 class="font-semibold text-gray-800">{{ employee.fullName }}</h3>
                <div class="flex space-x-2 space-x-reverse">
                  <button 
                    @click="editEmployee(employee)"
                    class="text-blue-600 hover:text-blue-800 p-1"
                  >
                    ✏️
                  </button>
                  <button 
                    @click="deleteEmployee(employee.id)"
                    class="text-red-600 hover:text-red-800 p-1"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <p class="text-gray-600 text-sm mb-2">{{ employee.phoneNumber }}</p>
              <div class="flex items-center justify-between text-xs text-gray-500">
                <span>تاریخ ثبت: {{ formatDate(employee.createdAt) }}</span>
                <span v-if="employee.isActive" class="text-green-600">فعال</span>
                <span v-else class="text-red-600">غیرفعال</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Section Form Modal -->
    <div v-if="showSectionForm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-4">
          {{ editingSection ? 'ویرایش بخش' : 'افزودن بخش جدید' }}
        </h3>
        
        <form @submit.prevent="saveSection" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">نام بخش</label>
            <input 
              v-model="sectionForm.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">توضیحات</label>
            <textarea 
              v-model="sectionForm.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            ></textarea>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">ترتیب نمایش</label>
            <input 
              v-model="sectionForm.displayOrder"
              type="number"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            />
          </div>
          
          <div class="flex items-center">
            <input 
              v-model="sectionForm.isActive"
              type="checkbox"
              class="mr-2"
            />
            <span class="text-sm text-gray-700">فعال</span>
          </div>

          <div class="flex space-x-3 space-x-reverse">
            <button 
              type="submit"
              class="flex-1 bg-brown-600 text-white py-2 rounded-lg font-medium hover:bg-brown-700 transition-colors"
            >
              {{ editingSection ? 'ویرایش' : 'افزودن' }}
            </button>
            <button 
              type="button"
              @click="closeSectionForm"
              class="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
            >
              انصراف
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Table Form Modal -->
    <div v-if="showTableForm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-4">
          {{ editingTable ? 'ویرایش میز' : 'افزودن میز جدید' }}
        </h3>
        
        <form @submit.prevent="saveTable" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">نام میز</label>
            <input 
              v-model="tableForm.name"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">توضیحات</label>
            <textarea 
              v-model="tableForm.description"
              rows="3"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            ></textarea>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">ظرفیت</label>
            <input 
              v-model="tableForm.capacity"
              type="number"
              min="1"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">ترتیب نمایش</label>
            <input 
              v-model="tableForm.displayOrder"
              type="number"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">بخش</label>
            <select 
              v-model="tableForm.sectionId"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brown-500 focus:border-transparent"
              required
            >
              <option value="">انتخاب کنید</option>
              <option v-for="section in sections" :key="section.id" :value="section.id">
                {{ section.name }}
              </option>
            </select>
          </div>
          
          <div class="flex items-center">
            <input 
              v-model="tableForm.isActive"
              type="checkbox"
              class="mr-2"
            />
            <span class="text-sm text-gray-700">فعال</span>
          </div>

          <div class="flex space-x-3 space-x-reverse">
            <button 
              type="submit"
              class="flex-1 bg-green-600 text-white py-2 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              {{ editingTable ? 'ویرایش' : 'افزودن' }}
            </button>
            <button 
              type="button"
              @click="closeTableForm"
              class="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
            >
              انصراف
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Employee Form Modal -->
    <div v-if="showEmployeeForm" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <h3 class="text-xl font-semibold text-gray-800 mb-4">
          {{ editingEmployee ? 'ویرایش کارمند' : 'افزودن کارمند جدید' }}
        </h3>
        
        <form @submit.prevent="saveEmployee" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">نام و نام خانوادگی</label>
            <input 
              v-model="employeeForm.fullName"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">شماره تلفن</label>
            <input 
              v-model="employeeForm.phoneNumber"
              type="tel"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              required
            />
          </div>
          
          <div class="flex items-center">
            <input 
              v-model="employeeForm.isActive"
              type="checkbox"
              class="mr-2"
            />
            <span class="text-sm text-gray-700">فعال</span>
          </div>

          <div class="flex space-x-3 space-x-reverse">
            <button 
              type="submit"
              class="flex-1 bg-purple-600 text-white py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              {{ editingEmployee ? 'ویرایش' : 'افزودن' }}
            </button>
            <button 
              type="button"
              @click="closeEmployeeForm"
              class="flex-1 bg-gray-300 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-400 transition-colors"
            >
              انصراف
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import moment from 'moment-jalaali'

export default {
  name: 'SectionTableManagement',
  setup() {
    const router = useRouter()
    const sections = ref([])
    const tables = ref([])
    const employees = ref([])
    const loading = ref(true)
    const error = ref(null)
    
    // Section form
    const showSectionForm = ref(false)
    const editingSection = ref(null)
    const sectionForm = ref({
      name: '',
      description: '',
      displayOrder: 0,
      isActive: true
    })
    
    // Table form
    const showTableForm = ref(false)
    const editingTable = ref(null)
    const tableForm = ref({
      name: '',
      description: '',
      capacity: 4,
      displayOrder: 0,
      sectionId: '',
      isActive: true
    })
    
    // Employee form
    const showEmployeeForm = ref(false)
    const editingEmployee = ref(null)
    const employeeForm = ref({
      fullName: '',
      phoneNumber: '',
      isActive: true
    })

    const loadData = async () => {
      try {
        loading.value = true
        error.value = null
        
        const [sectionsResponse, tablesResponse, employeesResponse] = await Promise.all([
          api.get('/sections'),
          api.get('/tables'),
          api.get('/employees')
        ])
        
        sections.value = sectionsResponse.data
        tables.value = tablesResponse.data
        employees.value = employeesResponse.data
      } catch (err) {
        console.error('Error loading data:', err)
        error.value = 'خطا در بارگذاری اطلاعات'
      } finally {
        loading.value = false
      }
    }

    // Section management
    const saveSection = async () => {
      try {
        if (editingSection.value) {
          await api.put(`/sections/${editingSection.value.id}`, sectionForm.value)
        } else {
          await api.post('/sections', sectionForm.value)
        }
        await loadData()
        closeSectionForm()
      } catch (error) {
        console.error('Error saving section:', error)
        alert('خطا در ذخیره بخش')
      }
    }

    const editSection = (section) => {
      editingSection.value = section
      sectionForm.value = {
        name: section.name,
        description: section.description,
        displayOrder: section.displayOrder,
        isActive: section.isActive
      }
      showSectionForm.value = true
    }

    const deleteSection = async (id) => {
      if (confirm('آیا از حذف این بخش اطمینان دارید؟')) {
        try {
          await api.delete(`/sections/${id}`)
          await loadData()
        } catch (error) {
          console.error('Error deleting section:', error)
          alert('خطا در حذف بخش')
        }
      }
    }

    const closeSectionForm = () => {
      showSectionForm.value = false
      editingSection.value = null
      sectionForm.value = {
        name: '',
        description: '',
        displayOrder: 0,
        isActive: true
      }
    }

    // Table management
    const saveTable = async () => {
      try {
        if (editingTable.value) {
          await api.put(`/tables/${editingTable.value.id}`, tableForm.value)
        } else {
          await api.post('/tables', tableForm.value)
        }
        await loadData()
        closeTableForm()
      } catch (error) {
        console.error('Error saving table:', error)
        alert('خطا در ذخیره میز')
      }
    }

    const editTable = (table) => {
      editingTable.value = table
      tableForm.value = {
        name: table.name,
        description: table.description,
        capacity: table.capacity,
        displayOrder: table.displayOrder,
        sectionId: table.sectionId,
        isActive: table.isActive
      }
      showTableForm.value = true
    }

    const deleteTable = async (id) => {
      if (confirm('آیا از حذف این میز اطمینان دارید؟')) {
        try {
          await api.delete(`/tables/${id}`)
          await loadData()
        } catch (error) {
          console.error('Error deleting table:', error)
          alert('خطا در حذف میز')
        }
      }
    }

    const closeTableForm = () => {
      showTableForm.value = false
      editingTable.value = null
      tableForm.value = {
        name: '',
        description: '',
        capacity: 4,
        displayOrder: 0,
        sectionId: '',
        isActive: true
      }
    }

    // Employee management
    const saveEmployee = async () => {
      try {
        if (editingEmployee.value) {
          await api.put(`/employees/${editingEmployee.value.id}`, employeeForm.value)
        } else {
          await api.post('/employees', employeeForm.value)
        }
        await loadData()
        closeEmployeeForm()
      } catch (error) {
        console.error('Error saving employee:', error)
        alert('خطا در ذخیره کارمند')
      }
    }

    const editEmployee = (employee) => {
      editingEmployee.value = employee
      employeeForm.value = {
        fullName: employee.fullName,
        phoneNumber: employee.phoneNumber,
        isActive: employee.isActive
      }
      showEmployeeForm.value = true
    }

    const deleteEmployee = async (id) => {
      if (confirm('آیا از حذف این کارمند اطمینان دارید؟')) {
        try {
          await api.delete(`/employees/${id}`)
          await loadData()
        } catch (error) {
          console.error('Error deleting employee:', error)
          alert('خطا در حذف کارمند')
        }
      }
    }

    const closeEmployeeForm = () => {
      showEmployeeForm.value = false
      editingEmployee.value = null
      employeeForm.value = {
        fullName: '',
        phoneNumber: '',
        isActive: true
      }
    }

    const goToAdminPanel = () => {
      router.push('/admin')
    }

    const goToMenu = () => {
      router.push('/')
    }

    const formatDate = (dateString) => {
      const m = moment(dateString)
      return m.format('jYYYY/jMM/jDD')
    }

    onMounted(() => {
      loadData()
    })

    return {
      sections,
      tables,
      employees,
      loading,
      error,
      showSectionForm,
      editingSection,
      sectionForm,
      showTableForm,
      editingTable,
      tableForm,
      showEmployeeForm,
      editingEmployee,
      employeeForm,
      loadData,
      saveSection,
      editSection,
      deleteSection,
      closeSectionForm,
      saveTable,
      editTable,
      deleteTable,
      closeTableForm,
      saveEmployee,
      editEmployee,
      deleteEmployee,
      closeEmployeeForm,
      goToAdminPanel,
      goToMenu,
      formatDate
    }
  }
}
</script>

<style scoped>
/* Custom styles if needed */
</style>
